import React, { useState } from 'react';

import 'core-js/stable';
import isNil from 'lodash/isNil';
import 'regenerator-runtime/runtime';
import 'url-search-params-polyfill';

import Button, { LoadingButton } from '@atlaskit/button';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';

import {
  InfoMessage,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '@qality/common-components';
import { PremiumFeatureGuard } from '@qality/common-components/src/atoms/PremiumFeatureGuard';
import { QalityFreeLimitsBanner } from '@qality/common-components/src/molecules/QalityFreeLimitsBanners';
import { IS_CLOUD } from '@qality/common-config';
import { messages } from '@qality/common-messages';
import { TestCycleOption } from '@qality/common-model/src/TestCycle';
import {
  StorageOfUniqueServices,
  TestCyclesService,
  TestExecutionService,
} from '@qality/common-service';

import { BannerWrapperStyled } from '../ExecutionDialog.styled';
import TestCaseForExecutionSelect from './TestCaseForExecutionSelect/TestCaseForExecutionSelect';
import { mapTestCycleForExecutionToOption } from './TestCaseForExecutionSelect/TestCaseForExecutionService';

type Props = {
  onClose: () => void;
  testCaseId: string;
  testCaseName: string;
};

export const PAGE_NUMBER = 0;
export const DEFAULT_SELECT_VALUE = {
  name: 'Ad hoc (no Test Cycle)',
};

export const TEST_EXECUTIONS_LIMIT_REACHED_CODE = 429;

export default function ExecutionDialog({
  testCaseName,
  testCaseId,
  onClose,
}: Props): JSX.Element {
  const [isExecuting, changeIsExecuting] = useState(false);
  const [isExecutionDisabled, changeIsExecutionDisabled] = useState(false);
  const [isExecutionsLimitReached, setIsExecutionsLimitReached] =
    useState(false);

  const [selectedOption, setSelectedOption] = useState<TestCycleOption>(
    mapTestCycleForExecutionToOption(DEFAULT_SELECT_VALUE)
  );

  const handleExecution = () => {
    changeIsExecutionDisabled(true);
    changeIsExecuting(true);
    submit();
  };

  const submit = async () => {
    try {
      let testExecutionId = selectedOption.value.testExecutionId;
      if (isNil(testExecutionId)) {
        const testExecution =
          selectedOption.value.testCaseInCycleId === null
            ? await TestExecutionService.createTestExecution(
                testCaseName,
                testCaseId
              )
            : await TestExecutionService.createTestExecution(
                testCaseName,
                testCaseId,
                selectedOption.value.testCaseInCycleId
              );
        testExecutionId = testExecution.id;
      }
      await goToTestExecutionPage(testExecutionId);
      if (IS_CLOUD) {
        window.AP.events.emit('soldevelo-issue-view-close-fullscreen');
      }
      onClose();
    } catch (e) {
      changeIsExecuting(false);
      await handleExecutionError(e);
    }
  };

  const handleExecutionError = async (e) => {
    if (e?.response?.status === 409) {
      const testCaseInCycleId =
        await TestCyclesService.fetchUnexecutedTestCaseInCycleId(
          testCaseId,
          selectedOption.value.testCycleId
        );
      if (!testCaseInCycleId) {
        StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
          e,
          messages['error.already.executed.TestCase.message']
        );
      } else {
        const newTestExecution = await TestExecutionService.createTestExecution(
          testCaseName,
          testCaseId,
          testCaseInCycleId
        );
        await goToTestExecutionPage(newTestExecution.id);
      }
    } else if (e?.response?.status === TEST_EXECUTIONS_LIMIT_REACHED_CODE) {
      setIsExecutionsLimitReached(true);
    } else {
      StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
        e,
        messages['error.executeTestCase.message']
      );
      onClose();
    }
  };

  const goToTestExecutionPage = async (testExecutionId) => {
    const url = await StorageOfUniqueServices.get(
      'URLService'
    ).getTestCaseExecutionUrl(testExecutionId);
    StorageOfUniqueServices.get('AP').navigator.go('site', {
      absoluteUrl: url,
    });
  };

  return (
    <ModalTransition>
      <Modal
        onClose={onClose}
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEscapePress={false}
        shouldScrollInViewport
      >
        <ModalHeader
          title='Execute'
          subtitle='Choose if you want to execute the Test Case in existing Test Cycle or Ad hoc (no Test Cycle).'
        />
        <ModalBody>
          <TestCaseForExecutionSelect
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
            testCaseId={testCaseId}
            onClose={onClose}
            isExecutionDisabled={isExecutionDisabled}
          />
          <InfoMessage>
            You can only select Test Cycles which already contain this Test
            Case.
          </InfoMessage>
          {isExecutionsLimitReached && (
            <PremiumFeatureGuard
              free={
                <BannerWrapperStyled>
                  <QalityFreeLimitsBanner
                    limitType='test-case-executions'
                    layout='section-banner'
                  />
                </BannerWrapperStyled>
              }
            />
          )}
        </ModalBody>
        <ModalFooter>
          <Button appearance='subtle' onClick={onClose}>
            Cancel
          </Button>
          <LoadingButton
            appearance='primary'
            onClick={handleExecution}
            isDisabled={isExecutionDisabled}
            isLoading={isExecuting}
            autoFocus
          >
            Execute
          </LoadingButton>
        </ModalFooter>
      </Modal>
    </ModalTransition>
  );
}
